import {
  CircleAlertIcon,
  FlaskConicalIcon,
  GitPullRequestIcon,
  HeartPulseIcon,
  ListChecksIcon,
  PackageCheckIcon,
  SearchIcon,
  ShieldCheckIcon,
  SunIcon,
  TriangleAlertIcon,
  ZapIcon,
  type LucideIcon,
} from "lucide-react";

import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { NO_ZOOM_FIELD_TEXT_CLASS, RESPONSIVE_TEXT_BUTTON_CLASS } from "~/lib/touchTarget";
import { cn } from "~/lib/utils";
import {
  AUTOMATION_TEMPLATE_CATEGORIES,
  formatTemplateSchedule,
  type AutomationTemplateCategoryFilter,
  type AutomationTemplateDefinition,
} from "./automationTemplates";

const TEMPLATE_ICONS: Readonly<Record<string, LucideIcon>> = {
  pr_babysitter: GitPullRequestIcon,
  daily_engineering_brief: ListChecksIcon,
  ci_failure_investigator: CircleAlertIcon,
  dependency_security_maintenance: ShieldCheckIcon,
  test_coverage_gaps: FlaskConicalIcon,
  release_readiness: PackageCheckIcon,
  production_error_triage: TriangleAlertIcon,
  weekly_codebase_health: HeartPulseIcon,
  morning_briefing: SunIcon,
  recurring_research_watch: SearchIcon,
};

interface CreateAutomationTemplateCatalogProps {
  readonly category: AutomationTemplateCategoryFilter;
  readonly mobileVisible: boolean;
  readonly query: string;
  readonly selectedKey: string | null;
  readonly templates: readonly AutomationTemplateDefinition[];
  readonly onBlank: () => void;
  readonly onCategoryChange: (category: AutomationTemplateCategoryFilter) => void;
  readonly onQueryChange: (query: string) => void;
  readonly onSelect: (template: AutomationTemplateDefinition) => void;
}

function TemplateIcon({ templateKey }: { readonly templateKey: string }) {
  const Icon = TEMPLATE_ICONS[templateKey] ?? ZapIcon;
  return <Icon className="size-5" aria-hidden="true" />;
}

export function CreateAutomationTemplateCatalog({
  category,
  mobileVisible,
  query,
  selectedKey,
  templates,
  onBlank,
  onCategoryChange,
  onQueryChange,
  onSelect,
}: CreateAutomationTemplateCatalogProps) {
  return (
    <section
      aria-label="Automation templates"
      className={cn(
        "flex min-h-0 w-[350px] shrink-0 flex-col border-r border-border bg-muted/24 max-[900px]:w-full max-[900px]:border-r-0",
        mobileVisible ? null : "max-[900px]:hidden",
      )}
    >
      <div className="space-y-3 border-b border-border p-4">
        <div className="relative">
          <SearchIcon
            className="pointer-events-none absolute start-3 top-1/2 z-10 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            aria-label="Search templates"
            className={cn(NO_ZOOM_FIELD_TEXT_CLASS, "[&_input]:ps-8")}
            placeholder="Search templates"
            type="search"
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
          />
        </div>
        <Button
          className={cn(RESPONSIVE_TEXT_BUTTON_CLASS, "w-full justify-start")}
          variant="outline"
          onClick={onBlank}
        >
          <ZapIcon className="size-4" />
          Blank automation
        </Button>
        <div className="flex flex-wrap gap-2" aria-label="Template categories">
          {AUTOMATION_TEMPLATE_CATEGORIES.map((item) => (
            <Button
              key={item}
              aria-pressed={category === item}
              className={cn(RESPONSIVE_TEXT_BUTTON_CLASS, "shrink-0")}
              size="sm"
              variant={category === item ? "secondary" : "ghost"}
              onClick={() => onCategoryChange(item)}
            >
              {item}
            </Button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between px-4 py-3 text-xs text-muted-foreground">
        <span>{templates.length === 1 ? "1 template" : `${templates.length} templates`}</span>
        <span>Editable</span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pb-3" role="listbox">
        {templates.length > 0 ? (
          <div className="space-y-1">
            {templates.map((template) => {
              const selected = selectedKey === template.key;
              return (
                <button
                  key={template.key}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={cn(
                    "grid min-h-[82px] w-full grid-cols-[32px_minmax(0,1fr)] gap-3 rounded-lg border border-transparent px-3 py-2.5 text-start outline-none transition-colors hover:bg-accent/64 focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/24",
                    selected ? "border-border bg-background shadow-xs/5" : null,
                  )}
                  onClick={() => onSelect(template)}
                >
                  <span
                    className={cn(
                      "mt-0.5 grid size-8 place-items-center rounded-lg border text-muted-foreground",
                      selected
                        ? "border-primary/24 bg-primary/8 text-primary"
                        : "border-border bg-muted/48",
                    )}
                  >
                    <TemplateIcon templateKey={template.key} />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-sm text-foreground">
                      {template.name}
                    </span>
                    <span className="mt-0.5 block truncate text-sm text-muted-foreground">
                      {template.summary}
                    </span>
                    <span className="mt-1 block truncate text-xs text-muted-foreground/80">
                      {formatTemplateSchedule(template.schedule)} ·{" "}
                      {template.connectors.join(" + ")}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <p className="px-3 py-10 text-center text-sm text-muted-foreground">No matches.</p>
        )}
      </div>
    </section>
  );
}
